import React from 'react'
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Hint } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { getDate, getMonth, getYear } from 'date-fns'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ProjectManagementMyProjectsPageManagerViewDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { useAsyncCanBulk } from 'modules/core/lib/useAsyncCan'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import EmployeeSelector from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/EmployeeSelector'
import { TimesheetTabsLayout } from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimesheetTabsLayout'
import employees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import TotalHoursHint from 'modules/project_management/components/hints/myProjects/TotalHoursHint'
import MyProjectsTable from 'modules/project_management/components/tables/MyProjectsTable'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import { originSection } from 'modules/project_management/lib/utils'
import { RouteContextParams } from 'modules/project_management/pages/MyProjectsRootPage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const MyProjectsPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { projectsStore, employeeLocationId } = useOutletContext<RouteContextParams>()
  const { year, month, day } = useParams()
  const { company } = useCurrentSession()
  const { employeeId } = useParams()
  const employeeIdNumber = Number(employeeId)
  const [hasGlobalTimeRecords, setHasGlobalTimeRecords] = React.useState<boolean>(false)

  const [hintStep, setHintStep] = React.useState<number>(0)
  const [withProjectsHintsAreHidden, setWithProjectsHintsAreHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideMyProjectsWithProjectsHint
  )
  const [withoutProjectsHintsAreHidden, setWithoutProjectsHintsAreHidden] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideMyProjectsWithoutProjectsHint)

  useEffect(() => {
    track(trackingEvents.projectManagement.flexibleTracking.visitMyProjectsSection, {
      origin: originSection(),
    })
    if (hintStep < 2 && (!withoutProjectsHintsAreHidden || !withProjectsHintsAreHidden))
      track(
        hasGlobalTimeRecords
          ? trackingEvents.projectManagement.flexibleTracking.hints.visitAddProjectHintWithProjects
          : trackingEvents.projectManagement.flexibleTracking.hints
              .visitAddProjectHintWithoutProjects,
        {
          origin: originSection(),
        }
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibleEmployees = employees.active
  const employee = employees.find((e) => {
    return e.attributes.id === employeeIdNumber
  })
  const { authorizations: validationAuthorizations } = useAsyncCanBulk(
    'attendance.read_period',
    visibleEmployees
  )
  const employeesWithAttendance = visibleEmployees.filter((e) => {
    return e.attributes.attendable && validationAuthorizations.for(e)
  })

  const { data: page, loading: loadingPage } = useQuery(
    ProjectManagementMyProjectsPageManagerViewDocument,
    {
      variables: {
        employeeId: employeeIdNumber,
        moduleId: AvailableModules.timeoff,
        companyId: company.id,
        featureIds: [Features.projects_flexible_tracking],
      },
    }
  )

  const hasMyProjectsFeature = !!page?.companies.featureAvailabilitiesConnection.nodes.find(
    (node) => node.id === Features.projects_flexible_tracking
  )?.available

  // Calulate new paths for the tab
  const pathnames = location.pathname.split('/')
  const isTimeTrackingView = pathnames.includes('time-tracking')

  if (!employee) return null

  const hasTimeOffModule = !!page?.companies.moduleAvailability?.available

  const canRegisterFlexibleTracking =
    page?.employees.employee?._policies?.registerFlexibleTracking.can ?? false

  const canReadMyProjects =
    page?.projectManagement._policies.globalTimeRecordRead.can ||
    page?.projectManagement._policies.flexibleTimeRecordRead.can
  if (hasMyProjectsFeature && !canReadMyProjects) return <NotFound />

  const myProjectsTypeNavigation = isTimeTrackingView
    ? navigation.timeTracking.employee.myProjects.yearMonthDayEmployeeId.fullPath
    : navigation.myProjects.employeeId.yearMonthDay.fullPath

  const buildWeekNavigation = (week: Date) => {
    const newWeek = {
      year: getYear(week),
      month: getMonth(week) + 1,
      day: getDate(week),
      employeeId: employeeIdNumber,
    }
    return navigate(myProjectsTypeNavigation(newWeek))
  }

  // Track hints
  const hasEmployeeGlobalTimeRecords = (hasGlobalTimeRecords: boolean) => {
    setHasGlobalTimeRecords(hasGlobalTimeRecords)
  }

  const handleHintClose = () => {
    hasGlobalTimeRecords
      ? setWithProjectsHintsAreHidden(true)
      : setWithoutProjectsHintsAreHidden(true)
    track(
      hasGlobalTimeRecords
        ? trackingEvents.projectManagement.flexibleTracking.hints.closeAddProjectHintWithProjects
        : trackingEvents.projectManagement.flexibleTracking.hints
            .closeAddProjectHintWithoutProjects,
      {
        origin: originSection(),
      }
    )
  }

  const handleHintNext = (nextStep: number) => {
    setHintStep(nextStep)
    track(
      hasGlobalTimeRecords
        ? trackingEvents.projectManagement.flexibleTracking.hints.nextProjectHintWithProjects
        : trackingEvents.projectManagement.flexibleTracking.hints.nextProjectHintWithoutProjects,
      {
        origin: originSection(),
      }
    )
  }

  const goToEmployeeTimesheetProjects = (employeeId: number) => {
    navigate(
      navigation.timeTracking.employee.myProjects.yearMonthDayEmployeeId.fullPath({
        year: Number(year),
        month: Number(month),
        day: Number(day),
        employeeId,
      })
    )
  }

  return (
    <ResponsivePage.Body
      variant="full"
      showHeader={!isTimeTrackingView}
      loading={false}
      error={null}
    >
      {isTimeTrackingView && (
        <Box width="full" alignItems="flexStart" paddingBottom="s24">
          <EmployeeSelector
            employee={employee}
            employees={employeesWithAttendance}
            goToEmployeeTimesheet={goToEmployeeTimesheetProjects}
          />
        </Box>
      )}
      <TimesheetTabsLayout>
        {loadingPage && <Loading />}
        {!loadingPage && (
          <Box width="full">
            <Hint.Popover.Provider
              steps={hasGlobalTimeRecords ? 5 : 4}
              isHidden={!!withoutProjectsHintsAreHidden || !!withProjectsHintsAreHidden}
              onChange={handleHintNext}
              onClose={handleHintClose}
              onFinish={handleHintClose}
            >
              {!hasMyProjectsFeature && (
                <UpsellingKit.EmptyState
                  moduleRequested={AvailableModules.projects}
                  featureRequested={Features.projects_flexible_tracking}
                  requestSource={ProjectsUpsellRequestSources.flexibleTrackingEmptyState}
                  requestReason={ValidReasons.MissingFeature}
                  title={i18n.t('project_management.upsell_banner.title')}
                  description={i18n.t('project_management.upsell_banner.subtitle')}
                />
              )}
              {hasMyProjectsFeature && projectsStore.hasProjectsAssigned && (
                <MyProjectsTable
                  projectsStore={projectsStore}
                  hasEmployeeGlobalTimeRecords={hasEmployeeGlobalTimeRecords}
                  hasGlobalTimeRecords={hasGlobalTimeRecords}
                  hasTimeOffModule={hasTimeOffModule}
                  canRegisterFlexibleTracking={canRegisterFlexibleTracking}
                  buildWeekNavigation={buildWeekNavigation}
                  employeeLocationId={employeeLocationId}
                />
              )}
              {hasMyProjectsFeature && !projectsStore.hasProjectsAssigned && <NotFound />}
              <Box position="relative" alignSelf="center" paddingRight="s300">
                <TotalHoursHint stepNumber={hasGlobalTimeRecords ? 5 : 4} />
              </Box>
            </Hint.Popover.Provider>
          </Box>
        )}
      </TimesheetTabsLayout>
      <Outlet />
    </ResponsivePage.Body>
  )
}
export default observer(MyProjectsPage)
